// Datetime helpers — render pickup times in the dispatcher's business
// timezone with a visible abbreviation (CDT/CST) so a customer in NYC booking
// a Wisconsin pickup never causes ambiguity.

export const BUSINESS_TZ = "America/Chicago";

const _fmt = new Intl.DateTimeFormat("en-US", {
  timeZone: BUSINESS_TZ,
  weekday: "short",
  month: "short",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
  timeZoneName: "short",
});

const _shortFmt = new Intl.DateTimeFormat("en-US", {
  timeZone: BUSINESS_TZ,
  month: "numeric",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
  timeZoneName: "short",
});

function _toDate(iso) {
  if (!iso) return null;
  // Naive timestamps like "2026-06-24T05:00" — the customer entered these in
  // the business TZ wall clock. Tag them with the correct UTC offset before
  // letting Date parse, so Intl displays them in the right slot.
  const naive = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/.test(iso);
  if (naive) {
    // Use a Date constructed via Intl to find the current UTC offset for
    // BUSINESS_TZ at that wall-clock instant.
    const [datePart, timePart] = iso.split("T");
    const [Y, M, D] = datePart.split("-").map(Number);
    const [h, m] = timePart.split(":").map(Number);
    // Build a UTC Date for the same wall-clock, then shift by the offset.
    // We measure the offset by formatting a probe.
    const utcGuess = Date.UTC(Y, M - 1, D, h, m);
    const probe = new Date(utcGuess);
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: BUSINESS_TZ, hour12: false, year: "numeric", month: "2-digit",
      day: "2-digit", hour: "2-digit", minute: "2-digit",
    }).formatToParts(probe);
    const pm = Object.fromEntries(parts.filter(p => p.type !== "literal").map(p => [p.type, p.value]));
    const businessUtc = Date.UTC(+pm.year, +pm.month - 1, +pm.day,
                                 +pm.hour === 24 ? 0 : +pm.hour, +pm.minute);
    const offsetMs = businessUtc - utcGuess;
    return new Date(utcGuess - offsetMs);
  }
  const d = new Date(iso);
  return isNaN(d.getTime()) ? null : d;
}

export function formatPickupLocal(iso) {
  const d = _toDate(iso);
  if (!d) return iso || "";
  return _fmt.format(d);
}

export function formatPickupShort(iso) {
  const d = _toDate(iso);
  if (!d) return iso || "";
  return _shortFmt.format(d);
}

export function tzAbbrev(iso) {
  const d = _toDate(iso) || new Date();
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: BUSINESS_TZ, timeZoneName: "short",
  }).formatToParts(d);
  const tz = parts.find(p => p.type === "timeZoneName");
  return tz ? tz.value : "CT";
}
